// Demo mode: the public "Try the demo" at app.canopybi.com/demo.
// Pure helpers: no screen code, no database, no browser storage.
//
// In demo mode the app never signs anyone in, never talks to the database or the server, and never saves
// anything. The numbers come from src/demoData.js. Review-list actions (assign, overhead, dismiss, restore, undo)
// work on screen only, so visitors can try them; the job totals follow, in whole cents, and a reload starts over.

import { toWholeCents } from "./money";
import { marginPercent } from "./aiCharts";

export const DEMO_PATH = "/demo";

// True when the address is the demo page: /demo, /demo/ or anything under /demo/. Case does not matter.
export function isDemoPath(pathname) {
  if (typeof pathname !== "string") return false;
  const p = pathname.toLowerCase().replace(/\/+$/, "");
  return p === DEMO_PATH || p.startsWith(`${DEMO_PATH}/`);
}

export const DEMO_BANNER_TEXT = "Demo business: sample numbers to show how Canopy works. Nothing here is saved.";
export const DEMO_BANNER_SHORT = "Demo business. Nothing is saved."; // the one-line version for phones
export const DEMO_SIGNUP_LABEL = "Get started";
export const DEMO_SIGNUP_HREF = "/";
export const DEMO_SAVE_NOTE = "Nothing is saved in the demo. Get started to use your own numbers.";
export const DEMO_NOT_SAVED_SUFFIX = "Nothing is saved in the demo.";
export const DEMO_AI_FREE_TEXT_REPLY = "In the demo I can answer the suggested questions. Sign up to ask about your own business.";
export const DEMO_UPLOAD_NOTE = "Uploading is turned off in the demo. Get started to bring in your own spreadsheet.";
export const DEMO_QUOTE_NOTE = "Saving quotes is turned off in the demo. You can still build a quote and print it.";

const normalize = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

// answers: the result of demoAnswers(data). Returns the matching answer text for a question typed or tapped,
// or null when it is not one of the suggested questions. Capital letters and spacing do not matter.
export function demoAnswerFor(answers, text) {
  const q = normalize(text);
  if (!q) return null;
  const hit = (answers || []).find((a) => normalize(a.question) === q);
  return hit ? hit.answer : null;
}

// ─── Review-list actions, on screen only ─────────────────────────────────────

const LISTS = ["autoMatched", "suggested", "untagged", "tagged", "overhead", "dismissed"];
// Cost rows a review item can have on a job: automatic matches and hand-tagged ones that start in the demo
// data, and the ones added here.
const purchaseIdsFor = (itemId) => [`demo-automatch-${itemId}`, `demo-tagged-${itemId}`, `demo-session-${itemId}`];

const cents = (n) => toWholeCents(n);

// Rebuild a job's totals from its purchases, labor, manual expenses and revenue, all in whole cents.
function refreshJob(job) {
  const sum = (list, pick) => list.reduce((s, x) => s + cents(pick(x)), 0);
  const revenueC = cents(job.revenue);
  const purchaseC = sum(job.purchases, (p) => p.Line[0].Amount);
  // Cash costs that count as labor are already inside job.laborCost; only the rest are materials.
  const manualC = sum((job.manualExpenses || []).filter((e) => !e.isLabor), (e) => e.amount);
  const laborC = cents(job.laborCost);
  const materialC = purchaseC + manualC;
  const costC = materialC + laborC;
  const vendorC = {};
  job.purchases.forEach((p) => { vendorC[p.EntityRef.name] = (vendorC[p.EntityRef.name] || 0) + cents(p.Line[0].Amount); });
  (job.manualExpenses || []).forEach((e) => { vendorC[e.vendor] = (vendorC[e.vendor] || 0) + cents(e.amount); });
  const costByVendor = {};
  Object.keys(vendorC).sort().forEach((v) => { costByVendor[v] = vendorC[v] / 100; });
  const margin = marginPercent(revenueC - costC, revenueC);
  return {
    ...job,
    materialCost: materialC / 100, costs: costC / 100, profit: (revenueC - costC) / 100,
    marginPct: margin === null ? "0.0" : margin.toFixed(1), costByVendor,
  };
}

// state: { jobSummaries, autoMatched, suggested, untagged, tagged, overhead, dismissed }.
// Moves one review item to a new list and keeps job costs right:
//   target "tagged"    (needs jobId): the cost goes onto that job, and comes off any job it was on before
//   target "overhead", "dismissed", "untagged": the cost comes off any job it was on
// Returns a new state (the old one is not changed). An unknown item or job returns the state as it was.
export function demoMoveItem(state, itemId, target, jobId) {
  let item = null;
  for (const name of LISTS) {
    item = (state[name] || []).find((x) => x.id === itemId);
    if (item) break;
  }
  if (!item) return state;
  if (target === "tagged" && !state.jobSummaries.some((j) => j.id === jobId)) return state;

  const next = { ...state };
  LISTS.forEach((name) => { next[name] = (state[name] || []).filter((x) => x.id !== itemId); });

  const ids = purchaseIdsFor(itemId);
  let jobs = state.jobSummaries.map((j) => {
    if (!j.purchases.some((p) => ids.includes(p.Id))) return j;
    return refreshJob({ ...j, purchases: j.purchases.filter((p) => !ids.includes(p.Id)) });
  });

  if (target === "tagged") {
    const purchase = {
      Id: `demo-session-${itemId}`, DocNumber: item.docNumber, TxnDate: item.date, TotalAmt: item.amount,
      PaymentType: item.paymentType, EntityRef: { name: item.vendor || "Unknown" },
      Line: [{ Amount: item.amount, Description: item.description, AccountBasedExpenseLineDetail: { CustomerRef: { value: jobId } } }],
    };
    jobs = jobs.map((j) => (j.id === jobId ? refreshJob({ ...j, purchases: [...j.purchases, purchase] }) : j));
    next.tagged = [...next.tagged, { ...item, taggedJobId: jobId, matchedBy: "manual", matchTier: null }];
  } else if (target === "overhead") {
    next.overhead = [...next.overhead, { ...item, taggedJobId: null }];
  } else if (target === "dismissed") {
    next.dismissed = [...next.dismissed, { ...item, taggedJobId: null }];
  } else if (target === "untagged") {
    next.untagged = [...next.untagged, { ...item, taggedJobId: null, matchedBy: null, matchTier: "needs_attention" }];
  } else {
    return state;
  }
  next.jobSummaries = jobs;
  delete next.totals; // the business totals from the demo data would be out of date after a move; the screens work them out from the jobs
  return next;
}
